import { reasonWarrantsInteraction } from './bearerBridge';
import type { BearerReason, BearerResult } from './bearerBridge';

export type ProfileAuthFailure = 'interactive-auth-required' | 'profile-unavailable';

export function classifyProfileAuthFailure(status: number, reason?: BearerReason): ProfileAuthFailure {
  if (status !== 401) return 'profile-unavailable';
  // An absent reason is reserved for a server rejection of a presented bearer.
  return reason === undefined || reasonWarrantsInteraction(reason)
    ? 'interactive-auth-required'
    : 'profile-unavailable';
}

interface ProfileGateDeps {
  getBearer(): Promise<BearerResult>;
  fetchProfile(token: string): Promise<Response>;
  startInteractiveAuth(reason: BearerReason): Promise<boolean>;
  isCancelled(): boolean;
}

export type ProfileGateResult =
  | { kind: 'profile'; response: Response }
  | { kind: 'redirecting' | 'cancelled' | 'error' };

/** Own bootstrap escalation without invoking the API client's token resolver twice. */
export async function loadAuthenticatedProfile(deps: ProfileGateDeps): Promise<ProfileGateResult> {
  try {
    if (deps.isCancelled()) return { kind: 'cancelled' };
    const bearer = await deps.getBearer();
    if (deps.isCancelled()) return { kind: 'cancelled' };

    let failure: ProfileAuthFailure;
    if (!bearer.token) {
      failure = classifyProfileAuthFailure(401, bearer.reason ?? 'identity-unknown');
    } else {
      const response = await deps.fetchProfile(bearer.token);
      if (deps.isCancelled()) return { kind: 'cancelled' };
      if (response.ok) return { kind: 'profile', response };
      failure = classifyProfileAuthFailure(response.status);
    }

    if (failure !== 'interactive-auth-required') return { kind: 'error' };
    if (deps.isCancelled()) return { kind: 'cancelled' };
    const started = await deps.startInteractiveAuth('interaction-required');
    if (deps.isCancelled()) return { kind: 'cancelled' };
    return { kind: started ? 'redirecting' : 'error' };
  } catch {
    return { kind: deps.isCancelled() ? 'cancelled' : 'error' };
  }
}
